import React, { ReactElement, useCallback, useEffect } from 'react';
// eslint-disable-next-line import/no-unresolved
import { Swiper, SwiperSlide } from 'swiper/react';
import {
  Autoplay,
  Navigation,
  Pagination,
  EffectFade,
  Keyboard,
  A11y,
} from 'swiper/modules';

import { bpHeroCarousel } from '../breakpoints';
import slugify from '../../../utils/slugify';
import {
  carouselAnalyticsEvents,
  addCarouselBulletAnalyticEvents,
} from '../utils/carouselAnalyticsEvents';
import { Section, Container, Carousel } from '../styles';

interface Props {
  data: Record<string, any>;
  BannerCard: (props: any) => ReactElement;
  fullWidth?: boolean;
}

const HeroCarousel = ({ data, BannerCard, fullWidth, ...rest }: Props) => {
  // Get sectionId and slugify for classNames
  const sectionId = data.analyticsID;
  const sectionTarget = slugify(sectionId ?? 'Carousel');

  const items = data.combinatorSectionItems.map((item) => (
    <SwiperSlide key={item.id} className={`hero-carousel ${item.bannerType}`}>
      <BannerCard
        data={item}
        className={`hero-carousel ${sectionTarget}`}
        section={sectionId}
        {...rest}
      />
    </SwiperSlide>
  ));

  const addAnalyticEvents = useCallback(
    () => addCarouselBulletAnalyticEvents(sectionTarget, sectionId),
    [sectionId, sectionTarget],
  );
  
  useEffect(addAnalyticEvents, [addAnalyticEvents]);

  return (
    <Section className="hero-carousel" data-testid="hero-carousel" $fullWidth={fullWidth}>
      <Container className="hero-carousel" $fullWidth={fullWidth}>
        <Carousel className={`hero-carousel ${sectionTarget}`} $fullWidth={fullWidth}>
          <Swiper
            tabIndex={0}
            speed={2000}
            breakpoints={bpHeroCarousel}
            navigation
            pagination={{
              clickable: true,
            }}
            autoplay={{
              delay: 6000,
            }}
            loop
            effect="fade"
            fadeEffect={{
              crossFade: true,
            }}
            a11y={{
              slideRole: 'group',
              enabled: true,
            }}
            allowTouchMove
            keyboard
            modules={[
              Autoplay,
              Navigation,
              Pagination,
              EffectFade,
              Keyboard,
              A11y,
            ]}
            className={`swiper ${sectionTarget}`}
            {...carouselAnalyticsEvents(sectionId)}
          >
            {items}
          </Swiper>
        </Carousel>
      </Container>
    </Section>
  );
};

export default HeroCarousel;
